import { memo, useState } from "react"
import { FiTrash2 } from "react-icons/fi"

import { useDeleteStudent } from "@/hooks/api/useStudents"
import type { DeleteStudentProps } from "@/types/student"
import { Button, Text } from "@chakra-ui/react"

import { ConfirmDialog } from "@/components/molecules/ConfirmDialog"

const DeleteStudent = memo(
  ({ student, onSuccess, onCancel }: DeleteStudentProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const deleteStudentMutation = useDeleteStudent()

    const displayLabel = student.first_name ?? student.id

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleConfirm = async () => {
      try {
        await deleteStudentMutation.mutateAsync(student.id)
        setIsOpen(false)
        onSuccess?.()
      } catch (error) {
        // Keep dialog open so user can retry after resolving error
        console.error("Error deleting student:", error)
      }
    }

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          variant="ghost"
          size="sm"
          colorPalette="red"
          aria-label={`Delete student: ${displayLabel}`}
        >
          <FiTrash2 fontSize="14px" aria-hidden="true" />
          Delete
        </Button>

        <ConfirmDialog
          isOpen={isOpen}
          onClose={handleClose}
          onConfirm={handleConfirm}
          title="Delete Student"
          message={
            <>
              <Text>
                Are you sure you want to delete <strong>{displayLabel}</strong>?
              </Text>
              <Text mt={2} fontSize="sm" color="gray.600">
                This action cannot be undone. This student will be permanently
                removed.
              </Text>
            </>
          }
          confirmText="Delete Student"
          confirmColorScheme="red"
          isLoading={deleteStudentMutation.isPending}
        />
      </>
    )
  },
)

DeleteStudent.displayName = "DeleteStudent"

export default DeleteStudent
export type { DeleteStudentProps }
